import React from 'react';
import HojaCaratula from './base/HojaCaratula';

// Etiquetas EXACTAS de la plantilla (templates/caratula/caratula_amparo_pnc.docx) y en su
// mismo orden: el modal es una vista previa, así que lo que se lee acá tiene que coincidir
// renglón por renglón con el papel. `guardable` marca los que además de imprimirse vuelven al
// cliente/trámite (ver CaratulaAmparoPncDocumentoService.persistirDatosGuardables).
//
// Son 17 renglones, la carátula más chica de la serie: no lleva aportes, ni CIDI, ni mail, ni
// los dos domicilios por separado. Sus cuatro renglones propios describen el expediente PNC
// demorado y el trámite del amparo.
const CAMPOS = {
    ingreso: { label: 'INGRESO', tipo: 'fecha' },
    pacto: { label: 'PACTO', tipo: 'sino' },

    claveFiscal: { label: 'CLAVE FISCAL', tipo: 'clave', guardable: true },
    claveCvss: { label: 'CLAVE CVSS', tipo: 'clave', guardable: true },
    referencia: { label: 'REFERENCIA', tipo: 'texto' },

    apellidoNombre: { label: 'APELLIDO/NOMBRE TITULAR', tipo: 'texto' },
    dniCuit: { label: 'DNI/CUIT', tipo: 'texto' },
    domicilio: {
        label: 'DOMICILIO', tipo: 'domicilio', estructurado: 'domicilioDniDatos', guardable: true,
        nota: 'Se guarda en el domicilio DNI del cliente (el barrio que ya tenga se conserva). Vacío no borra nada.',
    },
    telefono: {
        label: 'Nº TELEFONICO', tipo: 'texto',
        nota: 'Trae el teléfono propio y los contactos adicionales del cliente. Se editan desde la ficha del cliente.',
    },
    fechaNacimiento: { label: 'FECHA DE NACIMIENTO', tipo: 'fecha', guardable: true, noFutura: true },
    estadoCivil: { label: 'ESTADO CIVIL', tipo: 'estadoCivil', guardable: true },

    expedienteDemorado: { label: 'EXPEDIENTE DEMORADO', tipo: 'texto', guardable: true },
    fechaInicioExpediente: {
        label: 'FECHA DE INICIO DEL EXPEDIENTE', tipo: 'fecha',
        nota: 'Viene de "Fecha de inicio del trámite PNC". Lo que edites acá va solo al documento: para cambiarla de verdad, editá los datos del trámite.',
    },
    estadoActual: { label: 'ESTADO ACTUAL', tipo: 'texto', guardable: true },
    // Estos dos son LocalDate en el trámite y acá se tipean a mano, así que la carátula los
    // imprime pero no los guarda (ver CaratulaAmparoPncDocumentoService.persistirDatosGuardables).
    // Mismo caso que "FECHA DE INICIO DEL EXPEDIENTE", arriba: sin `guardable` y con una nota que
    // dice dónde cambiarlos de verdad. Antes decían `guardable: true` y el tooltip prometía
    // "Se guarda en el cliente/trámite", cosa que no pasaba: lo tipeado se perdía en silencio.
    prontoDespacho: {
        label: 'DIA QUE SE ENVIO EL PRONTO DESPACHO', tipo: 'fecha',
        nota: 'Viene de la fecha de envío del pronto despacho del trámite. Lo que edites acá va solo al documento: para cambiarla de verdad, editá los datos del trámite.',
    },
    envioAmparo: {
        label: 'DIA QUE SE ENVIO EL AMPARO', tipo: 'fecha',
        nota: 'Viene de la fecha de envío del amparo del trámite. Lo que edites acá va solo al documento: para cambiarla de verdad, editá los datos del trámite.',
    },
    observaciones: { label: 'OBSERVACIONES', tipo: 'multilinea', guardable: true },
};

// Una sola cajita de cabecera con INGRESO/PACTO, igual que en el papel.
const CAJAS_CABECERA = [
    ['ingreso', 'pacto'],
];

// Bloque principal: cada entrada es una fila. Un array de 2 keys = dos columnas, igual que
// los renglones compuestos del documento.
//
// CLAVE FISCAL/CVSS y REFERENCIA NO van acá: se dibujan aparte, arriba del recuadro.
const FILAS_CUERPO = [
    ['apellidoNombre'],
    ['dniCuit'],
    ['domicilio'],
    ['telefono'],
    ['fechaNacimiento'],
    ['estadoCivil'],
    ['expedienteDemorado'],
    ['fechaInicioExpediente', 'estadoActual'],
    ['prontoDespacho'],
    ['envioAmparo'],
    ['observaciones'],
];

// CLAVE FISCAL/CVSS y REFERENCIA van fuera del recuadro, tal como están en el papel.
const FILAS_PREVIAS = [
    ['claveFiscal', 'claveCvss'],
    // Esta carátula no tiene CIDI: la REFERENCIA va sola, a lo ancho.
    ['referencia'],
];

// Cómo se dibuja la hoja: título del documento, las cajitas de arriba, los renglones que
// van fuera del recuadro y el bloque principal. HojaCaratula pone el resto (modal, aviso,
// hoja A4 y botones), que es igual en las ocho carátulas.
const LAYOUT = {
    tituloDoc: 'ACCION DE AMPARO DE LA ADMINISTRACION.',
    leyendaDoc: 'TRAMITE: PNC',
    cabecera: CAJAS_CABECERA,
    filasPrevias: FILAS_PREVIAS,
    cuerpo: FILAS_CUERPO,
};

/**
 * Modal de la Carátula de Amparo PNC. Muestra los renglones prellenados (lo que ya está en el
 * cliente/trámite, o calculado desde los aportes) y en blanco lo que falta, para completar
 * antes de generar. Al confirmar, "Guardar y generar" persiste los renglones guardables y
 * emite el .docx con el DTO tal cual quedó en el formulario (WYSIWYG).
 */
export default function ModalCaratulaAmparoPnc(props) {
    return (
        <HojaCaratula
            {...props}
            titulo="Generar Carátula de Amparo PNC"
            nombreDocumento="la Carátula de Amparo PNC"
            campos={CAMPOS}
            layout={LAYOUT}
        />
    );
}
